import { Editor } from '@tiptap/core';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTrigger,
  Menu,
  MenuDivider,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tooltip,
} from '@fluentui/react-components';

import ToolbarMenuButton from '@modules/documents/components/header/toolbar/ToolbarMenuButton';
import useEditorActive from '@modules/documents/hooks/useEditorActive.hook';
import { TBorderStyle, TBorderWeight, BORDER_WEIGHTS, DEFAULT_BORDER_STYLE, DEFAULT_BORDER_WEIGHT } from './TableNode';
import {
  setTableBorderStyle,
  setCellsBorderStyle,
  setTableBorderWeight,
  setCellsBorderWeight,
  applyNoBorder,
  getBorderSelectionState,
  isCellSelection,
} from './borderCommands';
import { isWeightSupportedForStyle } from './borderRenderability';

type TProps = {
  editor: Editor;
  showDialog: boolean;
  showTooltip: boolean;
  isBubble?: boolean;
};

// --- Icons extracted from MS Word Online ---

const AllBordersIcon = () => (
  <svg height="20" width="20" viewBox="0,0,2048,2048" focusable="false">
    <path
      fill="currentColor"
      d="M102 256q0-64 45-109 45-45 109-45h1434q64 0 108 45 45 45 45 109v1434q0 64-45 108-45 45-108 45h-1434q-64 0-109-45-45-45-45-108v-1434z m154-51q-21 0-36 15-15 15-15 36v666h717v-717h-666z m666 819h-717v666q0 21 15 36 15 15 36 15h666v-717z m102 0v717h666q21 0 36-15 15-15 15-36v-666h-717z m717-102v-666q0-21-15-36-15-15-36-15h-666v717h717z"
    />
  </svg>
);

const NoBorderIcon = () => (
  <svg height="20" width="20" viewBox="0,0,2048,2048" focusable="false">
    <path
      fill="currentColor"
      d="M410 154q0 21-15 36-15 15-37 15-21 0-36-15-15-15-15-36 0-21 15-37 15-15 36-15 21 0 37 15 15 15 15 37z m-52 870q21 0 37-15 15-15 15-36 0-21-15-36-15-15-37-15-21 0-36 15-15 15-15 36 0 21 15 36 15 15 36 15z m-153 768q0-21-15-36-15-15-36-15-21 0-37 15-15 15-15 36 0 21 15 36 15 15 37 15 21 0 36-15 15-15 15-36z m153 51q21 0 37-15 15-15 15-36 0-21-15-36-15-15-37-15-21 0-36 15-15 15-15 36 0 21 15 36 15 15 36 15z m-204-307q21 0 36 15 15 15 15 36 0 21-15 36-15 15-36 15-21 0-37-15-15-15-15-36 0-21 15-36 15-15 37-15z m460 256q0 21-15 36-15 15-36 15-21 0-36-15-15-15-15-36 0-21 15-36 15-15 36-15 21 0 36 15 15 15 15 36z m0-819q0 21-15 36-15 15-36 15-21 0-36-15-15-15-15-36 0-21 15-36 15-15 36-15 21 0 36 15 15 15 15 36z m-51-768q21 0 36-15 15-15 15-36 0-21-15-37-15-15-36-15-21 0-36 15-15 15-15 37 0 21 15 36 15 15 36 15z m-358 1177q0-21-15-36-15-15-36-15-21 0-37 15-15 15-15 36 0 21 15 37 15 15 37 15 21 0 36-15 15-15 15-37z m563 461q21 0 36-15 15-15 15-36 0-21-15-36-15-15-36-15-21 0-36 15-15 15-15 36 0 21 15 36 15 15 36 15z m0-819q21 0 36-15 15-15 15-36 0-21-15-36-15-15-36-15-21 0-36 15-15 15-15 36 0 21 15 36 15 15 36 15z m51-870q0 21-15 36-15 15-36 15-21 0-36-15-15-15-15-36 0-21 15-37 15-15 36-15 21 0 36 15 15 15 15 37z m-665 972q21 0 36 15 15 15 15 37 0 21-15 36-15 15-36 15-21 0-37-15-15-15-15-36 0-21 15-37 15-15 37-15z m51-153q0-21-15-36-15-15-36-15-21 0-37 15-15 15-15 36 0 21 15 36 15 15 37 15 21 0 36-15 15-15 15-36z m973 870q21 0 36-15 15-15 15-36 0-21-15-36-15-15-36-15-21 0-37 15-15 15-15 36 0 21 15 36 15 15 37 15z m0-819q21 0 36-15 15-15 15-36 0-21-15-36-15-15-36-15-21 0-37 15-15 15-15 36 0 21 15 36 15 15 37 15z m51-870q0 21-15 36-15 15-36 15-21 0-37-15-15-15-15-36 0-21 15-37 15-15 37-15 21 0 36 15 15 15 15 37z m-1075 563q21 0 36 15 15 15 15 36 0 21-15 36-15 15-36 15-21 0-37-15-15-15-15-36 0-21 15-36 15-15 37-15z m1280 1075q0 21-15 36-15 15-37 15-21 0-36-15-15-15-15-36 0-21 15-36 15-15 36-15 21 0 37 15 15 15 15 36z m0-819q0 21-15 36-15 15-37 15-21 0-36-15-15-15-15-36 0-21 15-36 15-15 36-15 21 0 37 15 15 15 15 36z m-52-768q21 0 37-15 15-15 15-36 0-21-15-37-15-15-37-15-21 0-36 15-15 15-15 37 0 21 15 36 15 15 36 15z m-1177 358q0-21-15-36-15-15-36-15-21 0-37 15-15 15-15 36 0 21 15 36 15 15 37 15 21 0 36-15 15-15 15-36z m1382 1280q21 0 36-15 15-15 15-36 0-21-15-36-15-15-36-15-21 0-36 15-15 15-15 36 0 21 15 36 15 15 36 15z m0-819q21 0 36-15 15-15 15-36 0-21-15-36-15-15-36-15-21 0-36 15-15 15-15 36 0 21 15 36 15 15 36 15z m51-870q0 21-15 36-15 15-36 15-21 0-36-15-15-15-15-36 0-21 15-37 15-15 36-15 21 0 36 15 15 15 15 37z m-1484 153q21 0 36 15 15 15 15 36 0 21-15 37-15 15-36 15-21 0-37-15-15-15-15-37 0-21 15-36 15-15 37-15z m0-205q21 0 36 15 15 15 15 37 0 21-15 36-15 15-36 15-21 0-37-15-15-15-15-36 0-21 15-37 15-15 37-15z m1638 0q21 0 36 15 15 15 15 37 0 21-15 36-15 15-36 15-21 0-36-15-15-15-15-36 0-21 15-37 15-15 36-15z m0 205q21 0 36 15 15 15 15 36 0 21-15 37-15 15-36 15-21 0-36-15-15-15-15-37 0-21 15-36 15-15 36-15z m0 205q21 0 36 15 15 15 15 36 0 21-15 36-15 15-36 15-21 0-36-15-15-15-15-36 0-21 15-36 15-15 36-15z m0 205q21 0 36 15 15 15 15 36 0 21-15 36-15 15-36 15-21 0-36-15-15-15-15-36 0-21 15-36 15-15 36-15z m0 205q21 0 36 15 15 15 15 36 0 21-15 36-15 15-36 15-21 0-36-15-15-15-15-36 0-21 15-36 15-15 36-15z m51 256q0-21-15-37-15-15-36-15-21 0-36 15-15 15-15 37 0 21 15 36 15 15 36 15 21 0 36-15 15-15 15-36z m0 204q0-21-15-36-15-15-36-15-21 0-36 15-15 15-15 36 0 21 15 37 15 15 36 15 21 0 36-15 15-15 15-37z m0 205q0-21-15-36-15-15-36-15-21 0-36 15-15 15-15 36 0 21 15 36 15 15 36 15 21 0 36-15 15-15 15-36z m0 205q0-21-15-36-15-15-36-15-21 0-36 15-15 15-15 36 0 21 15 36 15 15 36 15 21 0 36-15 15-15 15-36z m-819-1638q0 21-15 36-15 15-36 15-21 0-36-15-15-15-15-36 0-21 15-37 15-15 36-15 21 0 36 15 15 15 15 37z m0 204q0 21-15 37-15 15-36 15-21 0-36-15-15-15-15-37 0-21 15-36 15-15 36-15 21 0 36 15 15 15 15 36z m0 205q0 21-15 36-15 15-36 15-21 0-36-15-15-15-15-36 0-21 15-36 15-15 36-15 21 0 36 15 15 15 15 36z m0 205q0 21-15 36-15 15-36 15-21 0-36-15-15-15-15-36 0-21 15-36 15-15 36-15 21 0 36 15 15 15 15 36z m0 205q0 21-15 36-15 15-36 15-21 0-36-15-15-15-15-36 0-21 15-36 15-15 36-15 21 0 36 15 15 15 15 36z m0 205q0 21-15 36-15 15-36 15-21 0-36-15-15-15-15-36 0-21 15-37 15-15 36-15 21 0 36 15 15 15 15 37z m0 204q0 21-15 37-15 15-36 15-21 0-36-15-15-15-15-37 0-21 15-36 15-15 36-15 21 0 36 15 15 15 15 36z m0 205q0 21-15 36-15 15-36 15-21 0-36-15-15-15-15-36 0-21 15-36 15-15 36-15 21 0 36 15 15 15 15 36z m0 205q0 21-15 36-15 15-36 15-21 0-36-15-15-15-15-36 0-21 15-36 15-15 36-15 21 0 36 15 15 15 15 36z"
    />
  </svg>
);

const WeightIcon = () => (
  <svg height="20" width="20" viewBox="0,0,2048,2048" focusable="false">
    <path
      fill="currentColor"
      d="M307 461q0-21 15-36 15-15 36-15h1332q21 0 36 15 15 15 15 36 0 21-15 36-15 15-36 15h-1332q-21 0-36-15-15-15-15-36z m0 1024q0-64 45-109 45-45 109-45h1126q64 0 109 45 45 45 45 109 0 64-45 108-45 45-109 45h-1126q-64 0-109-45-45-45-45-108z m103-666q-42 0-73 30-30 30-30 73 0 42 30 72 30 30 73 30h1228q42 0 73-30 30-30 30-72 0-42-30-73-30-30-73-30h-1228z"
    />
  </svg>
);

const StyleIcon = () => (
  <svg height="20" width="20" viewBox="0,0,2048,2048" focusable="false">
    <path
      fill="currentColor"
      d="M307 461q0-21 15-36 15-15 36-15h205q21 0 36 15 15 15 15 36 0 21-15 36-15 15-36 15h-205q-21 0-36-15-15-15-15-36z m512 0q0-21 15-36 15-15 36-15h308q21 0 36 15 15 15 15 36 0 21-15 36-15 15-36 15h-308q-21 0-36-15-15-15-15-36z m666-51q-21 0-36 15-15 15-15 36 0 21 15 36 15 15 36 15h205q21 0 36-15 15-15 15-36 0-21-15-36-15-15-36-15h-205z m-1178 563q0-21 15-36 15-15 36-15h1332q21 0 36 15 15 15 15 36 0 21-15 36-15 15-36 15h-1332q-21 0-36-15-15-15-15-36z m103 461q-42 0-73 30-30 30-30 72 0 42 30 72 30 30 73 30h1228q42 0 73-30 30-30 30-72 0-42-30-72-30-30-73-30h-1228z"
    />
  </svg>
);

// --- Line preview icons (exact MS Word Online SVGs) ---

const WeightLineIcon = ({ strokeWidth }: { strokeWidth: number }) => (
  <svg height="16" width="60" focusable="false" style={{ verticalAlign: 'middle' }}>
    <path stroke="currentColor" strokeWidth={strokeWidth} d="M7 8 l46 0" />
  </svg>
);

const WEIGHT_STROKE_MAP: Record<TBorderWeight, number> = {
  '0.5pt': 1,
  '1pt': 2,
  '2pt': 3,
  '3pt': 4,
};

const StyleLineIcon = ({ style }: { style: TBorderStyle }) => {
  if (style === 'double') {
    return (
      <svg height="16" width="60" focusable="false" style={{ verticalAlign: 'middle' }}>
        <path stroke="currentColor" strokeWidth={1} d="M7 6 l46 0" />
        <path stroke="currentColor" strokeWidth={1} d="M7 10 l46 0" />
      </svg>
    );
  }
  const dasharray = style === 'dashed' ? '5,3' : style === 'dotted' ? '1,1' : undefined;
  return (
    <svg height="16" width="60" focusable="false" style={{ verticalAlign: 'middle' }}>
      <path stroke="currentColor" strokeWidth={2} strokeDasharray={dasharray} d="M7 8 l46 0" />
    </svg>
  );
};

// --- Style/Weight labels ---

const STYLE_LABELS: Record<Exclude<TBorderStyle, 'none'>, string> = {
  solid: 'Solid',
  dashed: 'Dashed',
  dotted: 'Dotted',
  double: 'Double',
};

// --- Helpers ---

const applyStyle = (editor: Editor, style: TBorderStyle) => {
  if (isCellSelection(editor)) {
    setCellsBorderStyle(editor, style);
  } else {
    setTableBorderStyle(editor, style);
  }
  editor.commands.focus();
};

const applyWeight = (editor: Editor, weight: TBorderWeight) => {
  if (isCellSelection(editor)) {
    setCellsBorderWeight(editor, weight);
  } else {
    setTableBorderWeight(editor, weight);
  }
  editor.commands.focus();
};

const applyBorders = (editor: Editor) => {
  applyStyle(editor, 'solid');
};

const applyNoBorderAction = (editor: Editor) => {
  applyNoBorder(editor);
  editor.commands.focus();
};

// --- Component ---

const BordersMenuButton = ({ editor, showDialog, showTooltip, isBubble }: TProps) => {
  const currentBorderState = useEditorActive((ed) => getBorderSelectionState(ed));
  const currentStyle = currentBorderState?.style ?? DEFAULT_BORDER_STYLE;
  const currentWeight = currentBorderState?.weight ?? DEFAULT_BORDER_WEIGHT;
  const hasMixedStyle = currentBorderState?.style == null;
  const hasMixedWeight = currentBorderState?.weight == null;
  const doubleBorderWeightTooltip = 'Double borders use a 3pt minimum for reliable rendering.';
  const currentStyleLabel =
    currentBorderState?.style == null
      ? undefined
      : currentBorderState.style === 'none'
        ? 'No Border'
        : STYLE_LABELS[currentBorderState.style as keyof typeof STYLE_LABELS] || currentBorderState.style;
  const getIsWeightDisabled = (weight: TBorderWeight) =>
    currentBorderState?.hasDoubleBorder === true && !isWeightSupportedForStyle('double', weight);
  const renderWeightItem = (weight: TBorderWeight) => {
    const disabled = getIsWeightDisabled(weight);
    const item = (
      <MenuItem
        key={weight}
        onClick={() => applyWeight(editor, weight)}
        aria-checked={!hasMixedWeight && weight === currentWeight}
        disabled={disabled}
      >
        <span className="inline-flex items-center gap-2">
          <WeightLineIcon strokeWidth={WEIGHT_STROKE_MAP[weight]} />
          {weight}
        </span>
      </MenuItem>
    );

    if (!disabled) {
      return item;
    }

    return (
      <Tooltip
        key={weight}
        content={doubleBorderWeightTooltip}
        relationship="description"
        withArrow
        positioning="after"
      >
        <span className="block">{item}</span>
      </Tooltip>
    );
  };

  const triggerButton = (
    <ToolbarMenuButton icon={<AllBordersIcon />} tooltip={showTooltip && 'Border style'}></ToolbarMenuButton>
  );

  const menuContent = (
    <MenuList>
      <div className="flex gap-1">
        <Tooltip content="Borders" relationship="label">
          <Button appearance="subtle" icon={<AllBordersIcon />} onClick={() => applyBorders(editor)} />
        </Tooltip>
        <Tooltip content="No Border" relationship="label">
          <Button appearance="subtle" icon={<NoBorderIcon />} onClick={() => applyNoBorderAction(editor)} />
        </Tooltip>
      </div>
      <MenuDivider />

      {/* Weight submenu */}
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <MenuItem icon={<WeightIcon />} secondaryContent={hasMixedWeight ? undefined : currentWeight}>
            Weight
          </MenuItem>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>{BORDER_WEIGHTS.map(renderWeightItem)}</MenuList>
        </MenuPopover>
      </Menu>

      {/* Style submenu */}
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <MenuItem icon={<StyleIcon />} secondaryContent={currentStyleLabel}>
            Style
          </MenuItem>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {(Object.entries(STYLE_LABELS) as [TBorderStyle, string][]).map(([style, label]) => (
              <MenuItem
                key={style}
                onClick={() => applyStyle(editor, style)}
                aria-checked={!hasMixedStyle && style === currentStyle}
              >
                <span className="inline-flex items-center gap-2">
                  <StyleLineIcon style={style} />
                  {label}
                </span>
              </MenuItem>
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>
    </MenuList>
  );

  if (showDialog) {
    // Safari fallback: flat layout without nested submenus (submenus don't work in Dialog)
    const dialogContent = (
      <DialogTrigger disableButtonEnhancement>
        <MenuList>
          <div className="flex gap-1">
            <Tooltip content="Borders" relationship="label">
              <Button appearance="subtle" icon={<AllBordersIcon />} onClick={() => applyBorders(editor)} />
            </Tooltip>
            <Tooltip content="No Border" relationship="label">
              <Button appearance="subtle" icon={<NoBorderIcon />} onClick={() => applyNoBorderAction(editor)} />
            </Tooltip>
          </div>
          <MenuDivider />
          <MenuItem disabled>
            <span className="text-xs text-neutralFg-3">Weight</span>
          </MenuItem>
          {BORDER_WEIGHTS.map(renderWeightItem)}
          <MenuDivider />
          <MenuItem disabled>
            <span className="text-xs text-neutralFg-3">Style</span>
          </MenuItem>
          {(Object.entries(STYLE_LABELS) as [TBorderStyle, string][]).map(([style, label]) => (
            <MenuItem
              key={style}
              onClick={() => applyStyle(editor, style)}
              aria-checked={!hasMixedStyle && style === currentStyle}
            >
              <span className="inline-flex items-center gap-2">
                <StyleLineIcon style={style} />
                {label}
              </span>
            </MenuItem>
          ))}
        </MenuList>
      </DialogTrigger>
    );

    return (
      <Dialog>
        <DialogTrigger disableButtonEnhancement>{triggerButton}</DialogTrigger>
        <DialogSurface className="w-fit">{dialogContent}</DialogSurface>
      </Dialog>
    );
  }

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>{triggerButton}</MenuTrigger>
      <MenuPopover style={{ minWidth: 160 }}>{menuContent}</MenuPopover>
    </Menu>
  );
};

export default BordersMenuButton;
